import React, { useState, useEffect } from 'react';
import { phaseAPI, teamDirectoryAPI } from '../services/api';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  Plus, Check, ChevronRight, Clock, Calendar, User,
  GripVertical, X, Loader2, AlertTriangle
} from 'lucide-react';
import { DESIGN } from './DesignSystem';

// Phase Status Badge
const PhaseStatusBadge = ({ status, isOverdue }) => {
  const statusConfig = {
    'Planned': { bg: DESIGN.colors.background, text: DESIGN.colors.textSecondary, label: 'Planned' },
    'Active': { bg: DESIGN.colors.blueSubtle, text: DESIGN.colors.blue, label: 'Active' },
    'In Review': { bg: DESIGN.colors.amberSubtle, text: '#92400E', label: 'In Review' },
    'Completed': { bg: DESIGN.colors.successSubtle, text: DESIGN.colors.success, label: 'Completed' }
  };
  
  const config = statusConfig[status] || statusConfig.Planned;
  
  return (
    <div className="flex items-center gap-1">
      <span 
        className="text-xs font-semibold px-3 py-1 rounded-full"
        style={{ backgroundColor: config.bg, color: config.text }}
      >
        {config.label}
      </span>
      {isOverdue && (
        <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: DESIGN.colors.errorSubtle, color: DESIGN.colors.error }}>
          Overdue
        </span>
      )}
    </div>
  );
};

// Single Phase Item Component
const PhaseItem = ({ phase, onUpdate, onDelete, isReordering }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await phaseAPI.updatePhase(phase.id, { status: newStatus });
      onUpdate();
    } catch (error) {
      console.error('Error updating phase:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleComplete = () => {
    handleStatusChange('Completed');
  };
  
  return (
    <div 
      className={`group flex items-start gap-3 p-3 rounded-lg transition-all ${
        phase.status === 'Completed' ? 'bg-gray-50 opacity-75' : 'bg-white hover:shadow-sm'
      }`}
      style={{ border: '1px solid #E9ECEF' }}
      data-testid={`phase-item-${phase.id}`}
    >
      {/* Drag Handle */}
      {isReordering && (
        <div className="cursor-grab active:cursor-grabbing py-1" style={{ color: '#9CA3AF' }}>
          <GripVertical className="w-4 h-4" />
        </div>
      )}
      
      {/* Completion Checkbox */}
      <button
        onClick={handleComplete}
        disabled={loading || phase.status === 'Completed'}
        className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          phase.status === 'Completed' 
            ? 'bg-green-500 border-green-500' 
            : 'border-gray-300 hover:border-green-400'
        }`}
      >
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
        ) : phase.status === 'Completed' ? (
          <Check className="w-3 h-3 text-white" />
        ) : null}
      </button>
      
      {/* Phase Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p 
              className={`text-sm font-medium ${phase.status === 'Completed' ? 'line-through' : ''}`}
              style={{ color: phase.status === 'Completed' ? '#9CA3AF' : '#264653' }}
            >
              {phase.title}
            </p>
            {phase.description && (
              <p className="text-xs mt-1" style={{ color: '#6B7280' }}>{phase.description}</p>
            )}
          </div>
          
          {/* Status Badge */}
          <PhaseStatusBadge status={phase.status} isOverdue={phase.is_overdue} />
        </div>
        
        {/* Meta info */}
        <div className="flex items-center gap-4 mt-2">
          {/* Owner */}
          {phase.owner_name && (
            <div className="flex items-center gap-1 text-xs" style={{ color: '#6B7280' }}>
              <User className="w-3 h-3" />
              <span>{phase.owner_name}</span>
            </div>
          )}
          
          {/* Due Date */}
          {phase.due_date && (
            <div 
              className="flex items-center gap-1 text-xs"
              style={{ color: phase.is_overdue ? '#DC2626' : '#6B7280' }}
            >
              <Calendar className="w-3 h-3" />
              <span>
                {new Date(phase.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          )}
        </div>
        
        {/* Quick Actions (hidden until hover) */}
        {phase.status !== 'Completed' && (
          <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {phase.status === 'Planned' && (
              <button
                onClick={() => handleStatusChange('Active')}
                className="text-xs px-2 py-1 rounded hover:bg-blue-50"
                style={{ color: '#1E40AF' }}
              >
                Start
              </button>
            )}
            {phase.status === 'Active' && (
              <button
                onClick={() => handleStatusChange('In Review')}
                className="text-xs px-2 py-1 rounded hover:bg-amber-50"
                style={{ color: '#92400E' }}
              >
                Mark for Review
              </button>
            )}
            {phase.status === 'In Review' && (
              <button
                onClick={handleComplete}
                className="text-xs px-2 py-1 rounded hover:bg-green-50"
                style={{ color: '#059669' }}
              >
                Complete
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Delete Button */}
      <button
        onClick={() => onDelete(phase.id)}
        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-opacity"
        style={{ color: '#DC2626' }}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Add Phase Form
const AddPhaseForm = ({ taskId, onAdd, onCancel }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    setLoading(true);
    try {
      await phaseAPI.createPhase(taskId, {
        title: title.trim(),
        description: description.trim() || null,
        due_date: dueDate || null,
        status: 'Planned'
      });
      onAdd();
      setTitle('');
      setDescription('');
      setDueDate('');
    } catch (error) {
      console.error('Error creating phase:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form 
      onSubmit={handleSubmit}
      className="p-3 rounded-lg bg-gray-50"
      style={{ border: '1px dashed #D1D5DB' }}
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Phase title..."
        className="w-full text-sm px-2 py-1.5 rounded border bg-white"
        style={{ borderColor: '#E9ECEF', color: '#264653' }}
        autoFocus
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)..."
        className="w-full text-sm px-2 py-1.5 rounded border bg-white mt-2 resize-none"
        style={{ borderColor: '#E9ECEF', color: '#264653' }}
        rows={2}
      />
      <div className="flex items-center gap-2 mt-2">
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border bg-white"
          style={{ borderColor: '#E9ECEF', color: '#6B7280' }}
        />
        <div className="flex-1" />
        <button
          type="button"
          onClick={onCancel}
          className="text-xs px-3 py-1.5 rounded hover:bg-gray-200"
          style={{ color: '#6B7280' }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!title.trim() || loading}
          className="text-xs px-3 py-1.5 rounded text-white disabled:opacity-50"
          style={{ backgroundColor: '#2A9D8F' }}
        >
          {loading ? 'Adding...' : 'Add Phase'}
        </button>
      </div>
    </form>
  );
};

// Main Task Phases Section
const TaskPhasesSection = ({ taskId, onRefresh }) => {
  const [phases, setPhases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  
  const fetchPhases = async () => {
    try {
      const [phasesRes, summaryRes] = await Promise.all([
        phaseAPI.getTaskPhases(taskId),
        phaseAPI.getTaskPhasesSummary(taskId)
      ]);
      setPhases(phasesRes.data.phases || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching phases:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (taskId) {
      fetchPhases();
    }
  }, [taskId]);
  
  const handleRefresh = () => {
    fetchPhases();
    if (onRefresh) onRefresh();
  };
  
  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm('Delete this phase?')) return;
    
    try {
      await phaseAPI.deletePhase(phaseId);
      handleRefresh();
    } catch (error) {
      console.error('Error deleting phase:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2A9D8F' }} />
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="task-phases-section">
      {/* Header with Progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xs font-medium uppercase tracking-wider" style={{ color: '#6B7280' }}>
            Phases
          </h3>
          {summary && summary.total_phases > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}>
              {summary.completed_phases}/{summary.total_phases}
            </span>
          )}
          {summary?.has_overdue_phase && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
              <AlertTriangle className="w-3 h-3" />
              Overdue
            </span>
          )}
        </div>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => setShowAddForm(true)}
          className="h-7 text-xs gap-1"
          style={{ color: '#2A9D8F' }}
        >
          <Plus className="w-3 h-3" />
          Add Phase
        </Button>
      </div>
      
      {/* Progress Bar */}
      {summary && summary.total_phases > 0 && (
        <div>
          <Progress value={summary.progress_percent} className="h-1.5" />
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
            {summary.progress_percent}% complete
          </p>
        </div>
      )}
      
      {/* Phases List */}
      <div className="space-y-2">
        {phases.length === 0 && !showAddForm ? (
          <div 
            className="text-center py-6 rounded-lg"
            style={{ backgroundColor: '#F9FAFB', border: '1px dashed #D1D5DB' }}
          >
            <Clock className="w-6 h-6 mx-auto mb-2" style={{ color: '#9CA3AF' }} />
            <p className="text-sm" style={{ color: '#6B7280' }}>No phases yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="text-xs mt-2 hover:underline"
              style={{ color: '#2A9D8F' }}
            >
              Add your first phase
            </button>
          </div>
        ) : (
          phases.map((phase, idx) => (
            <PhaseItem
              key={phase.id}
              phase={phase}
              onUpdate={handleRefresh}
              onDelete={handleDeletePhase}
              isReordering={isReordering}
            />
          ))
        )}
        
        {/* Add Phase Form */}
        {showAddForm && (
          <AddPhaseForm 
            taskId={taskId}
            onAdd={() => {
              setShowAddForm(false);
              handleRefresh();
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
      
      {/* Next Due Phase */}
      {summary?.next_due_phase && (
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: '#92400E' }} />
            <span className="text-xs font-medium" style={{ color: '#92400E' }}>Next Up</span>
          </div>
          <p className="text-sm font-medium mt-1" style={{ color: '#78350F' }}>
            {summary.next_due_phase.title}
          </p>
          {summary.next_due_phase.due_date && (
            <p className="text-xs mt-1" style={{ color: '#92400E' }}>
              Due: {new Date(summary.next_due_phase.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Phase Progress Badge (for use in task tables)
export const PhaseProgressBadge = ({ phaseCount, completedPhaseCount, hasOverdue }) => {
  if (!phaseCount || phaseCount === 0) return null;
  
  const progress = Math.round((completedPhaseCount / phaseCount) * 100);
  
  return (
    <div className="flex items-center gap-1.5" data-testid="phase-progress-badge">
      <div 
        className="relative w-16 h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: '#E5E7EB' }}
      >
        <div 
          className="absolute left-0 top-0 h-full rounded-full transition-all"
          style={{ 
            width: `${progress}%`,
            backgroundColor: hasOverdue ? '#DC2626' : '#2A9D8F'
          }}
        />
      </div>
      <span 
        className="text-xs"
        style={{ color: hasOverdue ? '#DC2626' : '#6B7280' }}
      >
        {completedPhaseCount}/{phaseCount}
      </span>
      {hasOverdue && (
        <AlertTriangle className="w-3 h-3" style={{ color: '#DC2626' }} />
      )}
    </div>
  );
};

export default TaskPhasesSection;
